import React, { useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { DocumentEmailDA } from '../../services/admin/document.email.service';
import { GlobalSwal } from '../../styles/swal.style';
import { getError } from '../../languages/translations/response';
import { modalHide } from '../../utils/modal.util';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

export interface ModalSendEmailProps {
  companyId: string;
  documentType: 'invoices' | 'quotes' | 'orders' | 'provider-invoices' | 'provider-orders';
  documentId: string;
  documentNumber: string;
  recipientEmail?: string;
  companyName: string;
  companyEmail: string;
}

const ModalSendEmail: React.FunctionComponent<ModalSendEmailProps> = ({companyId, documentType, documentId, documentNumber, recipientEmail, companyName, companyEmail}) => {
  const {lang} = StateLanguage()

  const [to, setTo] = useState(recipientEmail || '')
  const [subject, setSubject] = useState(`${companyName} — ${documentNumber}`)
  const [message, setMessage] = useState(lang.labels.emailDefaultMessage)
  const [loading, setLoading] = useState(false)

  const handleClose = () => {
    modalHide('modal-send-email')
    setTimeout(() => {
      setTo(recipientEmail || '')
      setSubject(`${companyName} — ${documentNumber}`)
      setMessage(lang.labels.emailDefaultMessage)
    }, 200)
  }

  const handleSend = async () => {
    if (!to || !to.includes('@')) return

    setLoading(true)

    await DocumentEmailDA.sendEmail(companyId, documentType, documentId, to, subject, message, companyEmail).then((response) => {
      if (response.status === 200 || response.status === 201) {
        handleClose()

        GlobalSwal.fire({
          title: lang.labels.emailSent,
          text: lang.labels.actionCompletedReturningToPage,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200
        })
      } else {
        const errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    })

    setLoading(false)
  }

  return (
    <div id="modal-send-email" className="modal fade" tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered mw-650px">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="fw-bolder">{lang.labels.sendByEmail}</h2>
            <div className="btn btn-icon btn-sm btn-active-icon-primary" onClick={handleClose}>
              <span className="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black"/>
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black"/>
                </svg>
              </span>
            </div>
          </div>
          <div className="modal-body py-5 px-lg-10">
            <div className="mb-5">
              <label className="form-label fw-bolder">{lang.labels.recipient}</label>
              <input
                type="email"
                className="form-control"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="email@example.com"
              />
            </div>
            <div className="mb-5">
              <label className="form-label fw-bolder">{lang.labels.emailSubject}</label>
              <input
                type="text"
                className="form-control"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <div className="mb-5">
              <label className="form-label fw-bolder">{lang.labels.emailMessage}</label>
              <textarea
                className="form-control"
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-light me-3" type="button" onClick={handleClose}>{lang.labels.discard}</button>
            <button className="btn btn-primary" type="button" onClick={handleSend} disabled={loading || !to}>
              <div className="d-flex align-items-center">
                { loading
                  ?
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  :
                  <span className="svg-icon svg-icon-2 ms-0">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.43 8.56949L10.744 15.1395C10.6422 15.282 10.5804 15.4492 10.5651 15.6236C10.5498 15.7981 10.5815 15.9734 10.657 16.1315L13.194 21.4425C13.2737 21.6097 13.3991 21.751 13.5557 21.8499C13.7123 21.9488 13.8938 22.0014 14.079 22.0015H14.117C14.3087 21.9941 14.4941 21.9307 14.6502 21.8191C14.8062 21.7075 14.9261 21.5526 14.995 21.3735L21.933 3.33649C22.0011 3.15918 22.0164 2.96594 21.977 2.78013C21.9376 2.59432 21.8452 2.4239 21.711 2.28949L15.43 8.56949Z" fill="black"/>
                      <path opacity="0.3" d="M20.664 2.06648L2.62602 9.00148C2.44768 9.07085 2.29348 9.19082 2.1824 9.34663C2.07131 9.50244 2.00818 9.68731 2.00074 9.87853C1.99331 10.0697 2.04189 10.259 2.14054 10.4229C2.23919 10.5869 2.38359 10.7185 2.55601 10.8015L7.86601 13.3365C8.02383 13.4126 8.19925 13.4448 8.37382 13.4297C8.54839 13.4145 8.71565 13.3526 8.85801 13.2505L15.43 8.56548L21.711 2.28448C21.5762 2.15096 21.4055 2.05932 21.2198 2.02064C21.034 1.98196 20.8409 1.99788 20.664 2.06648Z" fill="black"/>
                    </svg>
                  </span>
                }
                {lang.labels.send}
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModalSendEmail;
